/*
  KALTA — first-touch channel attribution.  W-17.

  WHY THIS EXISTS
  ---------------
  Every capture form on this site already sends Klaviyo a `signup_source` property.
  That property records **which form was used** ("Footer", "PDP", "Gift Page") — it has
  never recorded **which channel delivered the visitor**. So a signup from a Google
  search and a signup from an Instagram link are byte-identical in Klaviyo, and
  SCORECARD.md L1 ("waitlist signups attributable to organic search") is unscoreable.
  Until this file exists, every statement about SEO's contribution to the waitlist is
  a guess.

  Three of the eight capture surfaces already merged `utm_*` from the URL. That does
  not fix it: UTMs only exist when someone tagged the link. Organic search, direct and
  assistant referrals all arrive with **no UTM parameters at all** — so the one channel
  this role is measured on was precisely the one the old code could never see.

  WHAT IT DOES
  ------------
  Records, once per visitor, the channel that first brought them to the site, and
  separately the channel that brought them back this session. Exposes both as a flat
  object of Klaviyo-safe scalar properties via `window.KALTA_ATTR()`.

  CONTRACT — read this before editing
  -----------------------------------
  1. `window.KALTA_ATTR()` MUST NEVER THROW and MUST ALWAYS return an object.
     Every caller is a live signup form. A signup that fails because attribution
     threw is a far worse outcome than a signup with no attribution. Callers use
     `Object.assign(props, (window.KALTA_ATTR && window.KALTA_ATTR()) || {})`, so a
     missing or broken script degrades to exactly today's behaviour.
  2. It writes NOTHING to the page and changes NO customer-facing copy.
  3. Storage is best-effort. Safari private mode, blocked storage and disabled
     cookies all fall through to a value computed from the current pageview.

  CHANNEL VALUES (closed set — do not add one without updating the Klaviyo segment)
     organic_search · ai_assistant · organic_social · referral · paid · email
     · affiliate · campaign · direct · internal

  ORDERING NOTE — the classifier checks AI assistants BEFORE search engines on
  purpose. `gemini.google.com` contains "google." and `edgeservices.bing.com`
  contains "bing.com"; classified in the other order, every Gemini and Copilot
  referral would be silently miscounted as organic search, which would inflate the
  exact number this role is measured on. Do not reorder these lists.
*/
(function () {
  'use strict';

  var FIRST_KEY = 'kalta_attr_v1';
  var SESSION_KEY = 'kalta_attr_session_v1';
  var VERSION = 'v1';

  /* Matched as substrings against the referrer hostname. Order within a list is
     irrelevant; order BETWEEN the lists is not — see the ordering note above. */
  var AI_ASSISTANT = [
    'chatgpt.com', 'chat.openai.com', 'openai.com', 'perplexity.ai', 'claude.ai',
    'copilot.microsoft.com', 'edgeservices.bing.com', 'gemini.google.com',
    'bard.google.com', 'you.com', 'poe.com', 'phind.com', 'andisearch.com'
  ];
  var SOCIAL = [
    'instagram.com', 'l.instagram.com', 'tiktok.com', 'youtube.com', 'youtu.be',
    'facebook.com', 'l.facebook.com', 'lm.facebook.com', 'linkedin.com', 'lnkd.in',
    'x.com', 'twitter.com', 't.co', 'pinterest.', 'reddit.com', 'out.reddit.com',
    'whatsapp.com', 'snapchat.com', 'threads.net', 'telegram.'
  ];
  var SEARCH = [
    'google.', 'bing.com', 'duckduckgo.com', 'search.yahoo', 'yahoo.com',
    'ecosia.org', 'search.brave.com', 'yandex.', 'baidu.com', 'naver.com',
    'qwant.com', 'startpage.com', 'mojeek.com', 'ask.com', 'aol.com'
  ];

  function hostOf(url) {
    try { return new URL(url, location.href).hostname.toLowerCase(); } catch (e) { return ''; }
  }

  function matches(host, list) {
    for (var i = 0; i < list.length; i++) {
      if (host.indexOf(list[i]) !== -1) { return true; }
    }
    return false;
  }

  function readParams() {
    var out = {};
    try {
      var p = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = p.get(k);
        if (v) { out[k] = String(v).slice(0, 100); }
      });
      /* Ad-click identifiers imply paid even with no utm_medium. */
      if (p.get('gclid')) { out._click = 'gclid'; }
      if (p.get('msclkid')) { out._click = 'msclkid'; }
      if (p.get('fbclid')) { out._click = 'fbclid'; }
    } catch (e) { /* URLSearchParams unavailable — fall through with {} */ }
    return out;
  }

  function classify(referrerHost, params) {
    var medium = (params.utm_medium || '').toLowerCase();

    if (medium) {
      if (medium === 'cpc' || medium === 'ppc' || medium === 'paid' || medium.indexOf('paid') === 0) { return 'paid'; }
      if (medium === 'email' || medium === 'e-mail' || medium === 'newsletter') { return 'email'; }
      if (medium === 'affiliate') { return 'affiliate'; }
      if (medium === 'organic') { return 'organic_search'; }
      if (medium.indexOf('social') !== -1 || medium === 'link-in-bio' || medium === 'link_in_bio') { return 'organic_social'; }
      return 'campaign';
    }
    /* gclid/msclkid/fbclid with no utm_medium is still a paid click. */
    if (params._click) { return 'paid'; }

    if (!referrerHost) { return 'direct'; }
    if (referrerHost === location.hostname.toLowerCase()) { return 'internal'; }
    if (matches(referrerHost, AI_ASSISTANT)) { return 'ai_assistant'; }
    if (matches(referrerHost, SOCIAL)) { return 'organic_social'; }
    if (matches(referrerHost, SEARCH)) { return 'organic_search'; }
    return 'referral';
  }

  function sourceOf(referrerHost, params, channel) {
    if (params.utm_source) { return String(params.utm_source).toLowerCase(); }
    if (channel === 'direct') { return '(direct)'; }
    if (channel === 'internal') { return '(internal)'; }
    return referrerHost.replace(/^www\./, '') || '(unknown)';
  }

  function mediumOf(params, channel) {
    if (params.utm_medium) { return String(params.utm_medium).toLowerCase(); }
    if (channel === 'organic_search') { return 'organic'; }
    if (channel === 'organic_social') { return 'social'; }
    if (channel === 'ai_assistant') { return 'assistant'; }
    if (channel === 'referral') { return 'referral'; }
    return 'none';
  }

  /* A snapshot of the CURRENT pageview. */
  function compute() {
    var params = readParams();
    var refHost = hostOf(document.referrer || '');
    var channel = classify(refHost, params);
    var iso = '';
    try { iso = new Date().toISOString().slice(0, 10); } catch (e) { iso = ''; }
    return {
      channel: channel,
      source: sourceOf(refHost, params, channel),
      medium: mediumOf(params, channel),
      campaign: params.utm_campaign || '',
      landing: (location.pathname || '/').slice(0, 200),
      at: iso,
      touches: 1
    };
  }

  function read(key, session) {
    try {
      var raw = (session ? sessionStorage : localStorage).getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function write(key, value, session) {
    try { (session ? sessionStorage : localStorage).setItem(key, JSON.stringify(value)); } catch (e) { /* best effort */ }
  }

  /* In-memory fallbacks so the getter still works with storage unavailable. */
  var memFirst = null;
  var memLast = null;

  function record() {
    var now = compute();

    var first = read(FIRST_KEY, false) || memFirst;
    var session = read(SESSION_KEY, true);
    var hadSession = !!session;

    /* FIRST TOUCH — written once, never overwritten. An internal navigation is not
       an entry point and must never be allowed to become the first touch. */
    if (!first && now.channel !== 'internal') {
      first = now;
      write(FIRST_KEY, first, false);
      memFirst = first;
    } else if (first && !hadSession && now.channel !== 'internal') {
      /* A returning visitor starting a new session: bump the visit counter but keep
         the original first-touch channel. */
      first.touches = (parseInt(first.touches, 10) || 1) + 1;
      write(FIRST_KEY, first, false);
      memFirst = first;
    }

    /* LAST TOUCH — the entry point for THIS session. A mid-session pageview with an
       empty referrer ('direct') must not overwrite a known session entry point;
       that is a stripped referrer, not a new visit. */
    if (now.channel !== 'internal') {
      if (!session || now.channel !== 'direct') {
        session = now;
        write(SESSION_KEY, session, true);
      }
    }
    if (!session) { session = now; write(SESSION_KEY, session, true); }
    memLast = session;

    return { first: first || session || now, last: session || now };
  }

  var state = null;
  try { state = record(); } catch (e) { state = null; }

  /*
    Public getter. Returns a flat object of Klaviyo-safe scalars.
    MUST NEVER THROW. See contract note 1 at the top of this file.
  */
  window.KALTA_ATTR = function () {
    try {
      var s = state;
      if (!s) {
        try { s = record(); } catch (e2) { s = null; }
      }
      if (!s) { return {}; }
      var f = s.first || {};
      var l = s.last || {};
      var out = {
        attr_first_channel: f.channel || 'unknown',
        attr_first_source: f.source || '(unknown)',
        attr_first_medium: f.medium || 'none',
        attr_first_landing: f.landing || '',
        attr_first_at: f.at || '',
        attr_last_channel: l.channel || 'unknown',
        attr_last_source: l.source || '(unknown)',
        attr_landing_page: (location.pathname || '/').slice(0, 200),
        attr_touches: parseInt(f.touches, 10) || 1,
        attr_version: VERSION
      };
      if (f.campaign) { out.attr_first_campaign = f.campaign; }
      if (l.campaign) { out.attr_last_campaign = l.campaign; }
      return out;
    } catch (e) { return {}; }
  };

  /*
    Convenience for GA4 event labels, so `generate_lead` carries the same channel
    string Klaviyo receives and the two datasets can be reconciled. Never throws.
  */
  window.KALTA_ATTR_CHANNEL = function () {
    try { return (window.KALTA_ATTR() || {}).attr_first_channel || 'unknown'; }
    catch (e) { return 'unknown'; }
  };
})();
